/**
 * <s-open-cart> — page-level behaviour for the slide-out cart (#cart-drawer):
 * - opens the drawer when the page URL ends in #cart. The cart page redirects shoppers here (SYNAPSE_01#cart),
 *   so /cart, checkout's "return to cart" and old cart links all land on the drawer.
 * - after the drawer closes from a tap or click, drops the focus the browser hands back to the button that opened it
 *   (the header cart icon or Add to cart), so no focus outline appears. Keyboard users keep the returned focus and outline.
 */
class SOpenCart extends HTMLElement {
  #ac;
  #pointer = false;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    document.addEventListener('pointerdown', () => { this.#pointer = true; }, { capture: true, signal });
    document.addEventListener('keydown', () => { this.#pointer = false; }, { capture: true, signal });

    const dialog = document.getElementById('cart-drawer');
    if (dialog) {
      dialog.addEventListener('close', () => {
        if (!this.#pointer) return;
        const dropFocus = () => {
          const active = document.activeElement;
          if (active instanceof HTMLElement && active !== document.body && !dialog.contains(active)) active.blur();
        };
        dropFocus();
        requestAnimationFrame(dropFocus);
        setTimeout(dropFocus, 50);
      }, { signal });
    }

    if (window.location.hash === '#cart') {
      const open = () => {
        const trigger = document.querySelector('button[commandfor="cart-drawer"][command="show-modal"]');
        if (trigger instanceof HTMLButtonElement) {
          trigger.click();
        } else if (dialog instanceof HTMLDialogElement && !dialog.open) {
          dialog.showModal();
        }
        history.replaceState(history.state, '', window.location.pathname + window.location.search);
      };
      if (document.readyState === 'complete') {
        open();
      } else {
        window.addEventListener('load', open, { once: true, signal });
      }
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }
}

if (!customElements.get('s-open-cart')) customElements.define('s-open-cart', SOpenCart);
